import { BOARD_SIZE, CANVAS_HEIGHT, CANVAS_WIDTH, activeBoardOriginX, activeBoardPixelSize, activeTilePixelSize } from "./constants";
import { areAdjacent } from "./grid";
import { getLayout } from "./layout";
import { RADIAL_HOLD_THRESHOLD_MS } from "./radialMenu";
import type { RadialMenuKind } from "./radialMenu";
import type { MoveAttempt, Position, PowerupKind, RotationDir } from "./types";

// Re-export all types from inputTypes for backward compatibility
export type {
  UiAction,
  Rect,
  UiRects,
  DragState,
  GameInput,
  BoardBounds,
  ImmediateGestureCallbacks,
  ResolvedUiHit,
  LeaderboardScrollGesture,
  SwipeIntentDirection
} from "./inputTypes";
export {
  SWIPE_INTENT_THRESHOLD_TILE_FRACTION,
  SWIPE_INTENT_AXIS_DOMINANCE_RATIO,
  SWIPE_INTENT_RELOCK_NEUTRAL_FRACTION,
  inferSwipeIntentTarget,
  resolveSwipeIntentDirection,
  swipeIntentDirectionToTarget
} from "./inputTypes";

// Local imports for use within this file
import type {
  UiAction,
  Rect,
  UiRects,
  DragState,
  GameInput,
  BoardBounds,
  ImmediateGestureCallbacks,
  ResolvedUiHit,
  LeaderboardScrollGesture,
  SwipeIntentDirection
} from "./inputTypes";
import {
  SWIPE_INTENT_THRESHOLD_TILE_FRACTION,
  SWIPE_INTENT_AXIS_DOMINANCE_RATIO,
  SWIPE_INTENT_RELOCK_NEUTRAL_FRACTION,
  inferSwipeIntentTarget,
  resolveSwipeIntentDirection,
  swipeIntentDirectionToTarget
} from "./inputTypes";

const POWERUP_SHORTCUT_ACTIONS: Partial<Record<string, UiAction>> = {
  Digit1: "ARM_POWERUP_HINT",
  Numpad1: "ARM_POWERUP_HINT",
  Digit2: "ARM_POWERUP_ROWRIP",
  Numpad2: "ARM_POWERUP_ROWRIP",
  Digit3: "ARM_POWERUP_COLCUT",
  Numpad3: "ARM_POWERUP_COLCUT",
  Digit4: "ARM_POWERUP_TRANSMUTE",
  Numpad4: "ARM_POWERUP_TRANSMUTE",
  Digit5: "ARM_POWERUP_ZIGZAGBOLT",
  Numpad5: "ARM_POWERUP_ZIGZAGBOLT"
};

const TOUCH_HIT_SLOP_PX = 12;
const DESKTOP_HIT_SLOP_PX = 6;

let activeUiHitSlopPx = 0;

function pointInRect(x: number, y: number, rect: Rect | undefined, slopOverride?: number): boolean {
  if (rect == null) return false;
  const slop = slopOverride ?? activeUiHitSlopPx;
  return (
    x >= rect.x - slop
    && y >= rect.y - slop
    && x <= rect.x + rect.w + slop
    && y <= rect.y + rect.h + slop
  );
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export class InputManager implements GameInput {
  private canvas: HTMLCanvasElement;
  private uiRects: UiRects = {};
  private nextUiRects: UiRects = {};
  private stableUiRects: UiRects = {};
  private uiTransitionActive = false;
  private blocked = false;
  private modalOpen = false;
  private _versusOptionsOpen = false;
  private swaps: MoveAttempt[] = [];
  private boardClicks: Position[] = [];
  private actions: UiAction[] = [];
  private musicVolumeChanges: number[] = [];
  private sfxVolumeChanges: number[] = [];
  private volumeDragTarget: "music" | "sfx" | null = null;
  _pendingFriendBattleIndex: number | null = null;
  _pendingFriendAcceptIndex: number | null = null;
  _pendingFriendDeclineIndex: number | null = null;
  _pendingFriendRemoveIndex: number | null = null;
  _pendingShopBuyIndex: number | null = null;
  _pendingShopEquipIndex: number | null = null;
  _pendingShopBuyGemsIndex: number | null = null;
  _pendingProfileEquipIndex: number | null = null;
  _pendingAvatarSelectNftIndex: number | null = null;
  private _pendingTokenShopItemIndex: number | null = null;
  private _tappedLobbyIndex: number | null = null;
  _tappedInboxIndex = -1;
  _tappedFriendIndex = -1;
  private pointerStartCell: Position | null = null;
  private pointerStartCanvasPos: { x: number; y: number } | null = null;
  private pointerLockedSwipeDirection: SwipeIntentDirection | null = null;
  private pointerCanvasPos: { x: number; y: number } | null = null;
  private activePointerId: number | null = null;
  private volumeDragPointerId: number | null = null;
  private leaderboardScrollPointerId: number | null = null;
  private leaderboardScrollLastY: number | null = null;
  private leaderboardScrollDeltaY = 0;
  private leaderboardScrollLastInteractionMs = 0;
  private _uiSwipeStartX: number | null = null;
  private _lastPressedRect: Rect | null = null;
  private _hoveredRect: Rect | null = null;
  private _radialHoldState: { kind: RadialMenuKind; pointerId: number; startX: number; startY: number; startTimeMs: number; holdReached: boolean } | null = null;
  private _pendingRadialRelease: { kind: RadialMenuKind; pointerX: number; pointerY: number; hubX: number; hubY: number } | null = null;
  private logicalWidth: number = CANVAS_WIDTH;
  private logicalHeight: number = CANVAS_HEIGHT;
  private _nativeSafeAreaTop = 0;
  private _nativeSafeAreaBottom = 0;
  /** Vertical offset applied to board hit detection (e.g. mobile versus lift) */
  private _boardYOffset = 0;
  private immediateGestureCallbacks: ImmediateGestureCallbacks = {};
  private readonly cleanupCallbacks: Array<() => void> = [];

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.bindEvents();
  }

  /** Update logical canvas size (call when canvas resizes for versus mode). */
  setLogicalSize(width: number, height: number): void {
    this.logicalWidth = width;
    this.logicalHeight = height;
  }

  /** Set board Y offset for visual shift (e.g. mobile versus lift). */
  setBoardYOffset(offset: number): void {
    this._boardYOffset = offset;
  }

  /** Set the native safe area insets in canvas logical pixels (iOS Dynamic Island + home indicator). */
  setNativeSafeAreaInsets(top: number, bottom: number): void {
    this._nativeSafeAreaTop = top;
    this._nativeSafeAreaBottom = bottom;
  }

  /** Effective layout height = logicalHeight minus both safe area insets. */
  private get effectiveLayoutHeight(): number {
    return this.logicalHeight - this._nativeSafeAreaTop - this._nativeSafeAreaBottom;
  }

  setUiRects(rects: UiRects): void {
    this.nextUiRects = { ...rects };
    if (this.uiTransitionActive) {
      return;
    }
    this.stableUiRects = this.nextUiRects;
    this.uiRects = this.nextUiRects;
  }

  setUiTransition(active: boolean): void {
    if (active === this.uiTransitionActive) {
      return;
    }
    this.uiTransitionActive = active;
    if (active) {
      this.uiRects = this.stableUiRects;
      return;
    }
    this.stableUiRects = this.nextUiRects;
    this.uiRects = this.nextUiRects;
  }

  getUiRects(): UiRects {
    return this.uiRects;
  }

  setBlocked(blocked: boolean): void {
    if (this.blocked === blocked) {
      return;
    }
    this.blocked = blocked;
    if (blocked) {
      this.clearBufferedInput();
      this.resetPointerTracking();
    }
  }

  /** Suppress canvas input while an external modal (e.g. AppKit) is open. */
  setModalOpen(open: boolean): void {
    this.modalOpen = open;
  }

  /** Tell input whether the versus options panel is currently open. */
  setVersusOptionsOpen(open: boolean): void {
    this._versusOptionsOpen = open;
  }

  setImmediateGestureCallbacks(callbacks: ImmediateGestureCallbacks): void {
    this.immediateGestureCallbacks = { ...callbacks };
  }

  private resolvePriorityUiHit(x: number, y: number): ResolvedUiHit | null {
    // Header heart icon — highest priority so overlapping page buttons don't steal the tap
    if (pointInRect(x, y, this.uiRects.staminaHeart, 0)) {
      return { action: "STAMINA_HEART_TAP", rect: this.uiRects.staminaHeart ?? null };
    }
    // Solo select page hits
    const soloSelectHits: Array<{ rect: Rect | undefined; action: UiAction }> = [
      { rect: this.uiRects.soloSelectBack, action: "SOLO_SELECT_BACK" },
      { rect: this.uiRects.soloCarouselPrev, action: "SOLO_CAROUSEL_PREV" },
      { rect: this.uiRects.soloCarouselNext, action: "SOLO_CAROUSEL_NEXT" },
      { rect: this.uiRects.soloSelectPlay, action: "START" },
    ];
    for (const hit of soloSelectHits) {
      if (pointInRect(x, y, hit.rect)) {
        return { action: hit.action, rect: hit.rect ?? null };
      }
    }
    // Block clicks from passing through the solo select overlay
    if (pointInRect(x, y, this.uiRects.soloSelectorBlocker)) {
      return { action: null, rect: this.uiRects.soloSelectorBlocker ?? null };
    }

    // Daily rewards screen hits
    if (pointInRect(x, y, this.uiRects.dailyRewardBack)) {
      return { action: "MAIN_MENU", rect: this.uiRects.dailyRewardBack ?? null };
    }
    for (let i = 0; i < 7; i++) {
      const rect = (this.uiRects as Record<string, Rect | undefined>)[`dailyRewardDay${i}`];
      if (pointInRect(x, y, rect)) {
        return { action: "CLAIM_DAILY_LOGIN_BONUS", rect: rect ?? null };
      }
    }
    if (pointInRect(x, y, this.uiRects.dailyRewardCrateTap)) {
      return { action: "DAILY_REWARD_CRATE_TAP", rect: this.uiRects.dailyRewardCrateTap ?? null };
    }
    if (pointInRect(x, y, this.uiRects.dailyRewardContinue)) {
      return { action: "DAILY_REWARD_CONTINUE", rect: this.uiRects.dailyRewardContinue ?? null };
    }

    if (pointInRect(x, y, this.uiRects.dailyLoginClaim)) {
      return { action: "CLAIM_DAILY_LOGIN_BONUS", rect: this.uiRects.dailyLoginClaim ?? null };
    }
    if (pointInRect(x, y, this.uiRects.dailyLoginSignIn)) {
      return { action: "OPEN_AUTH_GATE", rect: this.uiRects.dailyLoginSignIn ?? null };
    }
    if (pointInRect(x, y, this.uiRects.dailyLoginClose)) {
      return { action: "CLOSE_DAILY_LOGIN_BONUS", rect: this.uiRects.dailyLoginClose ?? null };
    }
    if (pointInRect(x, y, this.uiRects.dailyLoginDismiss)) {
      return { action: "CLOSE_DAILY_LOGIN_BONUS", rect: null };
    }
    if (pointInRect(x, y, this.uiRects.dailyLoginOpen)) {
      return { action: "OPEN_DAILY_LOGIN_BONUS", rect: this.uiRects.dailyLoginOpen ?? null };
    }
    return null;
  }

  private bindEvents(): void {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (this.blocked) {
        return;
      }
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }
      const powerupShortcutAction = POWERUP_SHORTCUT_ACTIONS[event.code];
      if (powerupShortcutAction != null) {
        event.preventDefault();
        this.actions.push(powerupShortcutAction);
        return;
      }
      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        event.preventDefault();
        this.actions.push(event.key === "ArrowLeft" ? "ROTATE_LEFT_NOW" : "ROTATE_RIGHT_NOW");
      }
      if (event.code === "Space") {
        event.preventDefault();
        this.actions.push("ACTIVATE_GROOVE");
      }
      if (event.key === "Enter") {
        this.actions.push("START");
      } else if (event.key.toLowerCase() === "m") {
        this.actions.push("TOGGLE_MUSIC");
      } else if (event.key.toLowerCase() === "p") {
        event.preventDefault();
        this.actions.push("TOGGLE_SETTINGS");
      } else if (event.key === "Escape") {
        event.preventDefault();
        // Context-aware back: go to previous screen / title
        if (this.uiRects.pauseMenuBounds != null) {
          this.actions.push("PAUSE_RESUME");
        } else if (this.uiRects.phoneBounds != null) {
          this.actions.push("PHONE_BACK");
        } else if (this.uiRects.settingsBack != null) {
          this.actions.push("MAIN_MENU");
        } else if (this.uiRects.mainMenu != null) {
          this.actions.push("MAIN_MENU");
        }
        // On title screen or gameplay with no back button — do nothing
      }
    };
    window.addEventListener("keydown", onKeyDown);
    this.cleanupCallbacks.push(() => window.removeEventListener("keydown", onKeyDown));

    const onKeyUp = (event: KeyboardEvent): void => {
      if (this.blocked) {
        return;
      }
      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        event.preventDefault();
      }
    };
    window.addEventListener("keyup", onKeyUp);
    this.cleanupCallbacks.push(() => window.removeEventListener("keyup", onKeyUp));

    const onContextMenu = (event: MouseEvent): void => {
      if (this.blocked) {
        event.preventDefault();
        return;
      }
      event.preventDefault();
      this.actions.push("CANCEL_POWERUP");
    };
    this.canvas.addEventListener("contextmenu", onContextMenu);
    this.cleanupCallbacks.push(() => this.canvas.removeEventListener("contextmenu", onContextMenu));

    const onPointerMove = (event: PointerEvent): void => {
      if (this.blocked || this.modalOpen) {
        this.resetPointerTracking();
        return;
      }
      const { x, y } = this.toCanvasPoint(event);
      this.pointerCanvasPos = { x, y };
      if (this.activePointerId != null && event.pointerId === this.activePointerId && this.pointerStartCell != null) {
        this.updateBoardSwipeIntentLock(x, y);
      }
      // Update hover state for desktop pointer feedback
      if (event.pointerType === "mouse") {
        const prevHovered = this._hoveredRect;
        this._hoveredRect = this.findHitRect(x, y);
        // Update cursor style when hover state changes
        if (this._hoveredRect !== prevHovered) {
          this.canvas.style.cursor = this._hoveredRect != null ? "pointer" : "";
        }
      }
      // Track hold elapsed for radial menu (holdReached flag)
      if (this._radialHoldState != null && event.pointerId === this._radialHoldState.pointerId) {
        const elapsed = performance.now() - this._radialHoldState.startTimeMs;
        if (elapsed >= RADIAL_HOLD_THRESHOLD_MS) {
          this._radialHoldState.holdReached = true;
        }
      }
      if (this.volumeDragPointerId != null && event.pointerId === this.volumeDragPointerId) {
        if (this.volumeDragTarget === "sfx") {
          this.captureSfxVolumeAtX(x);
        } else {
          this.captureMusicVolumeAtX(x);
        }
      }
      if (this.leaderboardScrollPointerId != null && event.pointerId === this.leaderboardScrollPointerId) {
        if (this.leaderboardScrollLastY != null && y !== this.leaderboardScrollLastY) {
          this.leaderboardScrollDeltaY += y - this.leaderboardScrollLastY;
          this.leaderboardScrollLastInteractionMs = performance.now();
        }
        this.leaderboardScrollLastY = y;
      }
    };
    this.canvas.addEventListener("pointermove", onPointerMove);
    this.cleanupCallbacks.push(() => this.canvas.removeEventListener("pointermove", onPointerMove));

    const onPointerDown = (event: PointerEvent): void => {
      if (this.blocked || this.modalOpen) {
        this.resetPointerTracking();
        return;
      }
      const { x, y } = this.toCanvasPoint(event);
      activeUiHitSlopPx = this.resolvePointerHitSlop(event.pointerType);
      try {
      // Rejoin overlay cancel button
      if (pointInRect(x, y, this.uiRects.rejoinCancel)) {
        this.actions.push("REJOIN_CANCEL");
        return;
      }
      // When the versus options panel is open, clicks outside the panel
      // (and outside the settings gear) dismiss it instead of interacting
      // with the board.
      if (this._versusOptionsOpen) {
        const inPanel = pointInRect(x, y, this.uiRects.versusOptionsBounds);
        const inGear = pointInRect(x, y, this.uiRects.settings, 0);
        if (!inPanel && !inGear) {
          this.actions.push("OPEN_SETTINGS");
          return;
        }
      }
      const boardCell = this.canvasToBoardCell(x, y);
      if (boardCell != null && this.isGameplayBoardInteractionActive() && !this._versusOptionsOpen) {
        this._lastPressedRect = null;
        if (this.activePointerId != null) return;
        this.activePointerId = event.pointerId;
        this.trySetPointerCapture(event.pointerId);
        this.pointerStartCell = boardCell;
        this.pointerStartCanvasPos = { x, y };
        this.pointerLockedSwipeDirection = null;
        return;
      }
      // Suppress UI button hits while a board drag is active (prevents
      // accidental back/forward taps when a finger drifts off the board).
      if (this.activePointerId != null && this.pointerStartCell != null) {
        this._lastPressedRect = null;
        return;
      }
      if (pointInRect(x, y, this.uiRects.leaderboardScrollViewport, 0)) {
        this._lastPressedRect = null;
        if (this.leaderboardScrollPointerId != null) {
          return;
        }
        this.leaderboardScrollPointerId = event.pointerId;
        this.leaderboardScrollLastY = y;
        this.leaderboardScrollLastInteractionMs = performance.now();
        this.trySetPointerCapture(event.pointerId);
        activeUiHitSlopPx = 0;
        return;
      }
      const priorityHit = this.resolvePriorityUiHit(x, y);
      if (priorityHit != null) {
        this._lastPressedRect = priorityHit.rect;
        if (priorityHit.action != null) {
          this.actions.push(priorityHit.action);
        }
        return;
      }
      this._lastPressedRect = this.findHitRect(x, y);
      // â”€â”€ Radial menu hub detection â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
      if (pointInRect(x, y, this.uiRects.characterHub)) {
        this._radialHoldState = {
          kind: "powerup",
          pointerId: event.pointerId,
          startX: x,
          startY: y,
          startTimeMs: performance.now(),
          holdReached: false
        };
        this.trySetPointerCapture(event.pointerId);
        activeUiHitSlopPx = 0;
        return;
      }
      if (pointInRect(x, y, this.uiRects.opponentHub)) {
        this._radialHoldState = {
          kind: "trap",
          pointerId: event.pointerId,
          startX: x,
          startY: y,
          startTimeMs: performance.now(),
          holdReached: false
        };
        this.trySetPointerCapture(event.pointerId);
        activeUiHitSlopPx = 0;
        return;
      }

      if (pointInRect(x, y, this.uiRects.musicVolume)) {
        this.volumeDragPointerId = event.pointerId;
        this.volumeDragTarget = "music";
        this.trySetPointerCapture(event.pointerId);
        this.captureMusicVolumeAtX(x);
        activeUiHitSlopPx = 0;
        return;
      }
      if (pointInRect(x, y, this.uiRects.sfxVolume)) {
        this.volumeDragPointerId = event.pointerId;
        this.volumeDragTarget = "sfx";
        this.trySetPointerCapture(event.pointerId);
        this.captureSfxVolumeAtX(x);
        activeUiHitSlopPx = 0;
        return;
      }
      if (pointInRect(x, y, this.uiRects.rotateLeft)) {
        this.actions.push("ROTATE_LEFT_NOW");
        return;
      }
      if (pointInRect(x, y, this.uiRects.rotateRight)) {
        this.actions.push("ROTATE_RIGHT_NOW");
        return;
      }
      if (
        pointInRect(x, y, this.uiRects.grooveMeter)
        || pointInRect(x, y, this.uiRects.grooveMixer)
      ) {
        this.actions.push("ACTIVATE_GROOVE");
        return;
      }
      if (pointInRect(x, y, this.uiRects.scoreRetry)) {
        this.actions.push("SCORE_RETRY_SUBMIT");
        return;
      }
      if (pointInRect(x, y, this.uiRects.gameOverNext)) {
        this.actions.push("GAMEOVER_NEXT");
        return;
      }
      if (pointInRect(x, y, this.uiRects.versusResultNext)) {
        this.actions.push("VERSUS_RESULT_NEXT");
        return;
      }
      if (pointInRect(x, y, this.uiRects.versusRematch)) {
        this.actions.push("VERSUS_REMATCH");
        return;
      }
      if (pointInRect(x, y, this.uiRects.versusExit)) {
        this.actions.push("VERSUS_EXIT");
        return;
      }
      if (pointInRect(x, y, this.uiRects.leaderboardToggle)) {
        this.actions.push("LEADERBOARD_TOGGLE");
        return;
      }
      if (pointInRect(x, y, this.uiRects.start)) {
        this.actions.push("START");
        activeUiHitSlopPx = 0;
        return;
      }
      if (pointInRect(x, y, this.uiRects.tutorial)) {
        this.actions.push("START_TUTORIAL");
        return;
      }
      if (pointInRect(x, y, this.uiRects.music)) {
        this.actions.push("TOGGLE_MUSIC");
        return;
      }
      if (pointInRect(x, y, this.uiRects.skipTutorial)) {
        this.actions.push("SKIP_TUTORIAL");
        return;
      }
      if (pointInRect(x, y, this.uiRects.loginGoogle)) {
        this.actions.push("LOGIN_GOOGLE");
        return;
      }
      if (pointInRect(x, y, this.uiRects.loginDiscord)) {
        this.actions.push("LOGIN_DISCORD");
        return;
      }
      if (pointInRect(x, y, this.uiRects.mainMenu)) {
        this.actions.push("MAIN_MENU");
        return;
      }
      if (pointInRect(x, y, this.uiRects.headerProfile, 0)) {
        this.actions.push("OPEN_PROFILE");
        return;
      }
      // Top-right header actions use strict hitboxes to prevent overlap misclicks.
      if (pointInRect(x, y, this.uiRects.settings, 0)) {
        this.actions.push("OPEN_SETTINGS");
        return;
      }
      if (pointInRect(x, y, this.uiRects.friends, 0)) {
        this.actions.push("OPEN_FRIENDS");
        return;
      }
      if (pointInRect(x, y, this.uiRects.coins, 0)) {
        this.actions.push("OPEN_GEM_SHOP");
        return;
      }
      if (this.uiRects.coins != null && pointInRect(x, y, this.uiRects.gemShop, 0)) {
        this.actions.push("OPEN_SHOP");
        return;
      }
      if (pointInRect(x, y, this.uiRects.settingsBack)) {
        this.actions.push("CLOSE_SETTINGS");
        return;
      }
      if (pointInRect(x, y, this.uiRects.settingsBgPrev)) {
        this.actions.push("SETTINGS_PAGE_PREV");
        return;
      }
      if (pointInRect(x, y, this.uiRects.settingsBgNext)) {
        this.actions.push("SETTINGS_PAGE_NEXT");
        return;
      }
      if (pointInRect(x, y, this.uiRects.settingsBgStyle)) {
        this.actions.push("SETTINGS_BG_STYLE_NEXT");
        return;
      }
      if (pointInRect(x, y, this.uiRects.settingsBgBloom)) {
        this.actions.push("SETTINGS_BG_BLOOM_NEXT");
        return;
      }
      if (pointInRect(x, y, this.uiRects.settingsBgGlow)) {
        this.actions.push("SETTINGS_BG_GLOW_NEXT");
        return;
      }
      if (pointInRect(x, y, this.uiRects.settingsBgSaturation)) {
        this.actions.push("SETTINGS_BG_SATURATION_NEXT");
        return;
      }
      if (pointInRect(x, y, this.uiRects.settingsBgMix)) {
        this.actions.push("SETTINGS_BG_MIX_NEXT");
        return;
      }
      if (pointInRect(x, y, this.uiRects.settingsBgMotion)) {
        this.actions.push("SETTINGS_BG_MOTION_NEXT");
        return;
      }
      if (pointInRect(x, y, this.uiRects.settingsPreset)) {
        this.actions.push("SETTINGS_PRESET_NEXT");
        return;
      }
      if (pointInRect(x, y, this.uiRects.settingsSideFx)) {
        this.actions.push("SETTINGS_SIDEFX_NEXT");
        return;
      }
      if (pointInRect(x, y, this.uiRects.settingsSideFxDensity)) {
        this.actions.push("SETTINGS_SIDEFX_DENSITY_NEXT");
        return;
      }
      if (pointInRect(x, y, this.uiRects.settingsParticles)) {
        this.actions.push("SETTINGS_PARTICLES_NEXT");
        return;
      }
      if (pointInRect(x, y, this.uiRects.settingsOpponent)) {
        this.actions.push("SETTINGS_OPPONENT_NEXT");
        return;
      }
      if (pointInRect(x, y, this.uiRects.settingsFpsCap)) {
        this.actions.push("SETTINGS_FPSCAP_NEXT");
        return;
      }
      if (pointInRect(x, y, this.uiRects.settingsDebugEnabled)) {
        this.actions.push("SETTINGS_DEBUG_ENABLED_TOGGLE");
        return;
      }
      if (pointInRect(x, y, this.uiRects.settingsDebugOverlayGameplay)) {
        this.actions.push("SETTINGS_DEBUG_OVERLAY_GAMEPLAY_TOGGLE");
        return;
      }
      if (pointInRect(x, y, this.uiRects.settingsDebugOverlayBot)) {
        this.actions.push("SETTINGS_DEBUG_OVERLAY_BOT_TOGGLE");
        return;
      }
      if (pointInRect(x, y, this.uiRects.settingsDebugVerbosity)) {
        this.actions.push("SETTINGS_DEBUG_VERBOSITY_NEXT");
        return;
      }
      if (pointInRect(x, y, this.uiRects.settingsDebugDiagnostics)) {
        this.actions.push("SETTINGS_DEBUG_DIAGNOSTICS_NEXT");
        return;
      }
      if (pointInRect(x, y, this.uiRects.settingsDebugPerfSafe)) {
        this.actions.push("SETTINGS_DEBUG_PERFSAFE_TOGGLE");
        return;
      }
      if (pointInRect(x, y, this.uiRects.titleWatchAd)) {
        this.actions.push("AD_GATE_WATCH_AD");
        return;
      }
      if (pointInRect(x, y, this.uiRects.editProfile)) {
        this.actions.push("EDIT_PROFILE");
        return;
      }
      if (pointInRect(x, y, this.uiRects.leaderboard)) {
        this.actions.push("VIEW_LEADERBOARD");
        return;
      }
      if (pointInRect(x, y, this.uiRects.leaderboardTabSolo)) {
        this.actions.push("LEADERBOARD_TAB_SOLO");
        return;
      }
      if (pointInRect(x, y, this.uiRects.leaderboardTabVersus)) {
        this.actions.push("LEADERBOARD_TAB_VERSUS");
        return;
      }
      if (pointInRect(x, y, this.uiRects.soloLeaderboardClassic)) {
        this.actions.push("SOLO_LEADERBOARD_CLASSIC");
        return;
      }
      if (pointInRect(x, y, this.uiRects.soloLeaderboardRowRush)) {
        this.actions.push("SOLO_LEADERBOARD_ROW_RUSH");
        return;
      }
      if (pointInRect(x, y, this.uiRects.soloLeaderboardClearout)) {
        this.actions.push("SOLO_LEADERBOARD_CLEAROUT");
        return;
      }
      if (pointInRect(x, y, this.uiRects.soloLeaderboardColorHunt)) {
        this.actions.push("SOLO_LEADERBOARD_COLOR_HUNT");
        return;
      }
      if (pointInRect(x, y, this.uiRects.soloLeaderboardTurboMaxxing)) {
        this.actions.push("SOLO_LEADERBOARD_TURBOMAXXING");
        return;
      }
      if (pointInRect(x, y, this.uiRects.soloLeaderboardCycleNext)) {
        this.actions.push("SOLO_LEADERBOARD_CYCLE_NEXT");
        return;
      }
      if (pointInRect(x, y, this.uiRects.worldMap)) {
        this.actions.push("OPEN_WORLD_MAP");
        return;
      }
      if (pointInRect(x, y, this.uiRects.worldMapBack)) {
        this.actions.push("WORLD_MAP_BACK");
        return;
      }
      if (pointInRect(x, y, this.uiRects.shopPrev)) {
        this.actions.push("SHOP_PREV");
        return;
      }
      if (pointInRect(x, y, this.uiRects.shopNext)) {
        this.actions.push("SHOP_NEXT");
        return;
      }
      if (pointInRect(x, y, this.uiRects.profileBrowsePrev)) {
        this.actions.push("PROFILE_BROWSE_PREV");
        return;
      }
      if (pointInRect(x, y, this.uiRects.profileBrowseNext)) {
        this.actions.push("PROFILE_BROWSE_NEXT");
        return;
      }
      if (pointInRect(x, y, this.uiRects.profilePortrait)) {
        this.actions.push("PROFILE_EDIT_SKIN");
        return;
      }
      if (pointInRect(x, y, this.uiRects.profileVinyl)) {
        this.actions.push("PROFILE_VINYL_FLIP");
        return;
      }
      if (pointInRect(x, y, this.uiRects.profileEditSkin)) {
        this.actions.push("PROFILE_EDIT_SKIN");
        return;
      }
      if (pointInRect(x, y, this.uiRects.profileViewAll)) {
        this.actions.push("PROFILE_VIEW_ALL");
        return;
      }
      if (pointInRect(x, y, this.uiRects.profileViewBrowse)) {
        this.actions.push("PROFILE_VIEW_BROWSE");
        return;
      }
      if (pointInRect(x, y, this.uiRects.messageAlertDismiss)) {
        this.actions.push("DISMISS_MESSAGE_ALERT");
        return;
      }
      if (pointInRect(x, y, this.uiRects.messageAlert)) {
        this.actions.push("TAP_MESSAGE_ALERT");
        return;
      }
      if (pointInRect(x, y, this.uiRects.phone)) {
        this.actions.push("OPEN_PHONE");
        return;
      }
      if (pointInRect(x, y, this.uiRects.phoneBack)) {
        this.actions.push("PHONE_BACK");
        return;
      }
      if (pointInRect(x, y, this.uiRects.phoneTabMessages)) {
        this.actions.push("PHONE_TAB_MESSAGES");
        return;
      }
      if (pointInRect(x, y, this.uiRects.phoneTabFriends)) {
        this.actions.push("PHONE_TAB_FRIENDS");
        return;
      }
      if (pointInRect(x, y, this.uiRects.phoneTabHighscores)) {
        this.actions.push("PHONE_TAB_HIGHSCORES");
        return;
      }
      if (pointInRect(x, y, this.uiRects.phoneTabSettings)) {
        this.actions.push("PHONE_TAB_SETTINGS");
        return;
      }
      if (pointInRect(x, y, this.uiRects.phoneTabWallet)) {
        this.actions.push("PHONE_TAB_WALLET");
        return;
      }
      if (pointInRect(x, y, this.uiRects.phoneTabTravel)) {
        this.actions.push("PHONE_TAB_TRAVEL");
        return;
      }
      if (pointInRect(x, y, this.uiRects.messageBack)) {
        this.actions.push("MESSAGE_BACK");
        return;
      }
      if (pointInRect(x, y, this.uiRects.messageAction)) {
        this.actions.push("MESSAGE_ACTION");
        return;
      }
      if (pointInRect(x, y, this.uiRects.messageActionSecondary)) {
        this.actions.push("MESSAGE_ACTION_SECONDARY");
        return;
      }
      // Inbox row taps
      for (let i = 0; i <= 5; i++) {
        const key = `inboxRow${i}` as keyof UiRects;
        if (pointInRect(x, y, this.uiRects[key] as Rect | undefined)) {
          this.actions.push("OPEN_MESSAGE");
          this._tappedInboxIndex = i;
          return;
        }
      }
      // Phone add friend inline
      if (pointInRect(x, y, this.uiRects.phoneAddFriendBack)) {
        this.actions.push("PHONE_ADD_FRIEND_BACK");
        return;
      }
      if (pointInRect(x, y, this.uiRects.phoneEditUsernameBack)) {
        this.actions.push("PHONE_EDIT_USERNAME_BACK");
        return;
      }
      if (pointInRect(x, y, this.uiRects.phoneAddFriendSubmit)) {
        this.actions.push("ADD_FRIEND");
        return;
      }
      if (pointInRect(x, y, this.uiRects.phoneReferFriend)) {
        this.actions.push("PHONE_REFER_FRIEND");
        return;
      }
      if (pointInRect(x, y, this.uiRects.phoneReferBack)) {
        this.actions.push("PHONE_REFER_BACK");
        return;
      }
      if (pointInRect(x, y, this.uiRects.phoneReferShare)) {
        this.immediateGestureCallbacks.onShareRequested?.();
        this.actions.push("PHONE_REFER_SHARE");
        return;
      }
      if (pointInRect(x, y, this.uiRects.phoneReferCopy)) {
        this.immediateGestureCallbacks.onCopyRequested?.();
        this.actions.push("PHONE_REFER_COPY");
        return;
      }
      // Onboarding
      if (pointInRect(x, y, this.uiRects.onboardingAcceptCall)) {
        this.actions.push("ONBOARDING_ACCEPT_CALL");
        return;
      }
      if (pointInRect(x, y, this.uiRects.onboardingSkip)) {
        this.actions.push("ONBOARDING_SKIP");
        return;
      }
      if (pointInRect(x, y, this.uiRects.onboardingTapZone)) {
        this.actions.push("ONBOARDING_TAP_ADVANCE");
        return;
      }
      if (pointInRect(x, y, this.uiRects.rafsbyWorldTour)) {
        this.actions.push("RAFSBY_WORLD_TOUR");
        return;
      }
      if (pointInRect(x, y, this.uiRects.rafsbyHowToPlay)) {
        this.actions.push("RAFSBY_HOW_TO_PLAY");
        return;
      }
      if (pointInRect(x, y, this.uiRects.debugResetOnboarding)) {
        this.actions.push("DEBUG_RESET_ONBOARDING");
        return;
      }
      // Friend detail view
      if (pointInRect(x, y, this.uiRects.friendDetailBack)) {
        this.actions.push("FRIEND_DETAIL_BACK");
        return;
      }
      if (pointInRect(x, y, this.uiRects.friendDetailRemove)) {
        this.actions.push("FRIEND_DETAIL_REMOVE");
        return;
      }
      if (pointInRect(x, y, this.uiRects.friendDetailBattle)) {
        this.actions.push("FRIEND_DETAIL_BATTLE");
        return;
      }
      // Friend row taps (open detail)
      for (let i = 0; i <= 4; i++) {
        const key = `friendRow${i}` as keyof UiRects;
        if (pointInRect(x, y, this.uiRects[key] as Rect | undefined)) {
          this.actions.push("OPEN_FRIEND_DETAIL");
          this._tappedFriendIndex = i;
          return;
        }
      }
      // Pause menu buttons (checked before phone bounds so pause takes priority)
      if (pointInRect(x, y, this.uiRects.pauseResume)) {
        this.actions.push("PAUSE_RESUME");
        return;
      }
      if (pointInRect(x, y, this.uiRects.pauseSettingsBack)) {
        this.actions.push("PAUSE_SETTINGS_BACK");
        return;
      }
      if (pointInRect(x, y, this.uiRects.pauseSettings)) {
        this.actions.push("PAUSE_SETTINGS");
        return;
      }
      if (pointInRect(x, y, this.uiRects.pauseRestart)) {
        this.actions.push("PAUSE_RESTART");
        return;
      }
      if (pointInRect(x, y, this.uiRects.pauseQuit)) {
        this.actions.push("PAUSE_QUIT");
        return;
      }
      if (this.uiRects.pauseMenuBounds && !pointInRect(x, y, this.uiRects.pauseMenuBounds)) {
        this.actions.push("PAUSE_RESUME");
        return;
      }
      if (this.uiRects.phoneBounds && !pointInRect(x, y, this.uiRects.phoneBounds)) {
        this.actions.push("PHONE_BACK");
        return;
      }
      if (pointInRect(x, y, this.uiRects.logout)) {
        this.actions.push("LOGOUT");
        return;
      }
      if (pointInRect(x, y, this.uiRects.versusMode)) {
        this.actions.push("VERSUS_MODE");
        return;
      }
      if (pointInRect(x, y, this.uiRects.versusReady)) {
        this.actions.push("VERSUS_READY");
        return;
      }
      if (pointInRect(x, y, this.uiRects.versusLeave)) {
        this.actions.push("VERSUS_LEAVE");
        return;
      }
      if (pointInRect(x, y, this.uiRects.versusClose)) {
        this.actions.push("VERSUS_LEAVE");
        return;
      }
      if (pointInRect(x, y, this.uiRects.versusChat)) {
        this.actions.push("VERSUS_CHAT");
        return;
      }
      if (pointInRect(x, y, this.uiRects.versusCreateLobby)) {
        this.actions.push("VERSUS_CREATE_LOBBY");
        return;
      }
      if (pointInRect(x, y, this.uiRects.lobbyToggleJoin)) {
        this.actions.push("LOBBY_TOGGLE_JOIN");
        return;
      }
      if (pointInRect(x, y, this.uiRects.versusJoinLobby)) {
        this.actions.push("VERSUS_JOIN_LOBBY");
        return;
      }
      if (this.uiRects.versusLobbyRows != null) {
        for (let i = 0; i < this.uiRects.versusLobbyRows.length; i++) {
          if (pointInRect(x, y, this.uiRects.versusLobbyRows[i])) {
            this._tappedLobbyIndex = i;
            this.actions.push("VERSUS_TAP_LOBBY_ROW");
            return;
          }
        }
      }
      if (pointInRect(x, y, this.uiRects.versusRefreshLobbies)) {
        this.actions.push("VERSUS_REFRESH_LOBBIES");
        return;
      }
      if (pointInRect(x, y, this.uiRects.versusQuickmatch)) {
        this.actions.push("VERSUS_QUICKMATCH");
        return;
      }
      if (pointInRect(x, y, this.uiRects.versusForfeit)) {
        this.actions.push("VERSUS_FORFEIT");
        return;
      }
      if (pointInRect(x, y, this.uiRects.shop)) {
        this.actions.push("OPEN_SHOP");
        return;
      }
      if (pointInRect(x, y, this.uiRects.shopBack)) {
        this.actions.push("MAIN_MENU");
        return;
      }
      if (this.uiRects.coins == null && pointInRect(x, y, this.uiRects.gemShop)) {
        this.actions.push("OPEN_GEM_SHOP");
        return;
      }
      if (pointInRect(x, y, this.uiRects.gemShopBack)) {
        this.actions.push("MAIN_MENU");
        return;
      }
      if (pointInRect(x, y, this.uiRects.openTokenShop)) {
        this.actions.push("OPEN_TOKEN_SHOP");
        return;
      }
      if (pointInRect(x, y, this.uiRects.tokenShopBack)) {
        this.actions.push("TOKEN_SHOP_BACK");
        return;
      }
      if (pointInRect(x, y, this.uiRects.shopExchange)) {
        this.actions.push("SHOP_EXCHANGE");
        return;
      }
      for (let i = 0; i < 6; i++) {
        const buyKey = `shopBuy${i}` as keyof Omit<UiRects, 'versusLobbyRows' | 'tokenShopItem'>;
        if (pointInRect(x, y, this.uiRects[buyKey] as Rect | undefined)) {
          this.actions.push("SHOP_BUY");
          this._pendingShopBuyIndex = i;
          return;
        }
        const equipKey = `shopEquip${i}` as keyof Omit<UiRects, 'versusLobbyRows' | 'tokenShopItem'>;
        if (pointInRect(x, y, this.uiRects[equipKey] as Rect | undefined)) {
          this.actions.push("SHOP_EQUIP");
          this._pendingShopEquipIndex = i;
          return;
        }
      }
      for (let i = 0; i < 4; i++) {
        const key = `shopBuyGems${i}` as keyof Omit<UiRects, 'versusLobbyRows' | 'tokenShopItem'>;
        if (pointInRect(x, y, this.uiRects[key] as Rect | undefined)) {
          this.actions.push("SHOP_BUY_GEMS");
          this._pendingShopBuyGemsIndex = i;
          return;
        }
      }
      for (let i = 0; i < 4; i++) {
        const key = `shopBuySol${i}` as keyof Omit<UiRects, 'versusLobbyRows' | 'tokenShopItem'>;
        if (pointInRect(x, y, this.uiRects[key] as Rect | undefined)) {
          this.actions.push("SHOP_BUY_SOL");
          this._pendingShopBuyGemsIndex = i;
          return;
        }
      }
      // IAP purchase dialog buttons (must be checked BEFORE shopBuyIap so overlays take priority)
      if (pointInRect(x, y, this.uiRects.shopIapConfirm)) {
        this.actions.push("SHOP_IAP_CONFIRM");
        return;
      }
      if (pointInRect(x, y, this.uiRects.shopIapCancel)) {
        this.actions.push("SHOP_IAP_CANCEL");
        return;
      }
      if (pointInRect(x, y, this.uiRects.shopIapDismiss)) {
        this.actions.push("SHOP_IAP_CANCEL"); // Dismiss reuses cancel action to reset state
        return;
      }
      for (let i = 0; i < 4; i++) {
        const key = `shopBuyIap${i}` as keyof Omit<UiRects, 'versusLobbyRows' | 'tokenShopItem'>;
        if (pointInRect(x, y, this.uiRects[key] as Rect | undefined)) {
          this.actions.push("SHOP_BUY_IAP");
          this._pendingShopBuyGemsIndex = i;
          return;
        }
      }
      if (pointInRect(x, y, this.uiRects.shopRestoreIap)) {
        this.actions.push("SHOP_RESTORE_IAP");
        return;
      }
      if (pointInRect(x, y, this.uiRects.profile)) {
        this.actions.push("OPEN_PROFILE");
        return;
      }
      if (pointInRect(x, y, this.uiRects.settingsTutorial)) {
        this.actions.push("START_TUTORIAL");
        return;
      }
      if (pointInRect(x, y, this.uiRects.settingsLogout)) {
        this.actions.push("LOGOUT");
        return;
      }
      if (pointInRect(x, y, this.uiRects.settingsMusic)) {
        this.actions.push("TOGGLE_MUSIC");
        return;
      }
      if (pointInRect(x, y, this.uiRects.settingsSfx)) {
        this.actions.push("TOGGLE_SFX");
        return;
      }
      if (pointInRect(x, y, this.uiRects.settingsHaptics)) {
        this.actions.push("TOGGLE_HAPTICS");
        return;
      }
      for (let i = 0; i < 12; i++) {
        const key = `profileEquip${i}` as keyof Omit<UiRects, 'versusLobbyRows' | 'tokenShopItem'>;
        if (pointInRect(x, y, this.uiRects[key] as Rect | undefined, 0)) {
          this.actions.push("PROFILE_EQUIP");
          this._pendingProfileEquipIndex = i;
          return;
        }
      }
      if (pointInRect(x, y, this.uiRects.profilePagePrev)) {
        this.actions.push("PROFILE_PAGE_PREV");
        return;
      }
      if (pointInRect(x, y, this.uiRects.profilePageNext)) {
        this.actions.push("PROFILE_PAGE_NEXT");
        return;
      }
      if (pointInRect(x, y, this.uiRects.profileBack)) {
        this.actions.push("MAIN_MENU");
        return;
      }
      if (pointInRect(x, y, this.uiRects.profileSettings)) {
        this.actions.push("OPEN_PROFILE_SETTINGS");
        return;
      }
      if (pointInRect(x, y, this.uiRects.profileFilterAll)) {
        this.actions.push("PROFILE_FILTER_ALL");
        return;
      }
      if (pointInRect(x, y, this.uiRects.profileFilterWallet)) {
        this.actions.push("PROFILE_FILTER_WALLET");
        return;
      }
      if (pointInRect(x, y, this.uiRects.profileSettingsBack)) {
        this.actions.push("MAIN_MENU");
        return;
      }
      if (pointInRect(x, y, this.uiRects.changeUsername)) {
        this.actions.push("CHANGE_USERNAME");
        return;
      }
      if (pointInRect(x, y, this.uiRects.toggleAppearOffline)) {
        this.actions.push("TOGGLE_APPEAR_OFFLINE");
        return;
      }
      if (pointInRect(x, y, this.uiRects.togglePushNotifications)) {
        this.actions.push("TOGGLE_PUSH_NOTIFICATIONS");
        return;
      }
      if (pointInRect(x, y, this.uiRects.walletViewSkins)) {
        this.actions.push("EDIT_PROFILE");
        return;
      }
      if (pointInRect(x, y, this.uiRects.walletConnect)) {
        this.actions.push("WALLET_CONNECT");
        return;
      }
      if (pointInRect(x, y, this.uiRects.walletUnlink)) {
        this.actions.push("WALLET_UNLINK");
        return;
      }
      if (pointInRect(x, y, this.uiRects.walletSync)) {
        this.actions.push("NFT_SYNC");
        return;
      }
      if (pointInRect(x, y, this.uiRects.solWalletConnect)) {
        this.actions.push("SOL_WALLET_CONNECT");
        return;
      }
      if (pointInRect(x, y, this.uiRects.solWalletDisconnect)) {
        this.actions.push("SOL_WALLET_DISCONNECT");
        return;
      }
      // Token shop purchase dialog buttons (must be checked BEFORE tokenShopItem rows)
      if (pointInRect(x, y, this.uiRects.tokenShopConfirm)) {
        this.actions.push("TOKEN_SHOP_CONFIRM");
        return;
      }
      if (pointInRect(x, y, this.uiRects.tokenShopCancel)) {
        this.actions.push("TOKEN_SHOP_CANCEL");
        return;
      }
      if (pointInRect(x, y, this.uiRects.tokenShopRecheck)) {
        this.actions.push("TOKEN_SHOP_RECHECK");
        return;
      }
      if (pointInRect(x, y, this.uiRects.tokenShopDismiss)) {
        this.actions.push("TOKEN_SHOP_DISMISS");
        return;
      }
      if (this.uiRects.tokenShopItem != null) {
        for (let i = 0; i < this.uiRects.tokenShopItem.length; i++) {
          if (pointInRect(x, y, this.uiRects.tokenShopItem[i])) {
            this._pendingTokenShopItemIndex = i;
            this.actions.push("TOKEN_SHOP_SELECT");
            return;
          }
        }
      }
      for (let i = 0; i < 50; i++) {
        const key = `avatarSelectNft${i}` as keyof Omit<UiRects, 'versusLobbyRows' | 'tokenShopItem'>;
        if (pointInRect(x, y, this.uiRects[key] as Rect | undefined, 0)) {
          this.actions.push("AVATAR_SELECT_NFT");
          this._pendingAvatarSelectNftIndex = i;
          return;
        }
      }
      if (pointInRect(x, y, this.uiRects.adGateWatchAd)) {
        this.actions.push("AD_GATE_WATCH_AD");
        return;
      }
      if (pointInRect(x, y, this.uiRects.adGateConnectWallet)) {
        this.actions.push("AD_GATE_CONNECT_WALLET");
        return;
      }
      if (pointInRect(x, y, this.uiRects.shareReferral)) {
        this.actions.push("SHARE_REFERRAL");
        return;
      }
      if (pointInRect(x, y, this.uiRects.installApp)) {
        this.actions.push("INSTALL_APP");
        return;
      }
      if (pointInRect(x, y, this.uiRects.usernameInput)) {
        this.actions.push("FOCUS_USERNAME_INPUT");
        return;
      }
      if (pointInRect(x, y, this.uiRects.usernameClaim)) {
        this.actions.push("CLAIM_USERNAME");
        return;
      }
      // Friend remove confirmation modal actions (checked before other FRIENDS actions).
      if (pointInRect(x, y, this.uiRects.friendRemoveConfirm)) {
        this.actions.push("FRIEND_REMOVE_CONFIRM");
        return;
      }
      if (pointInRect(x, y, this.uiRects.friendRemoveCancel)) {
        this.actions.push("FRIEND_REMOVE_CANCEL");
        return;
      }
      if (pointInRect(x, y, this.uiRects.friendRemoveDismiss)) {
        this.actions.push("FRIEND_REMOVE_CANCEL");
        return;
      }
      if (pointInRect(x, y, this.uiRects.friendsBack)) {
        this.actions.push("MAIN_MENU");
        return;
      }
      if (pointInRect(x, y, this.uiRects.openAddFriend)) {
        this.actions.push("OPEN_ADD_FRIEND");
        return;
      }
      if (pointInRect(x, y, this.uiRects.addFriendBack)) {
        this.actions.push("ADD_FRIEND_BACK");
        return;
      }
      if (pointInRect(x, y, this.uiRects.addFriendSubmit)) {
        this.actions.push("ADD_FRIEND");
        return;
      }
      for (let i = 0; i < 5; i++) {
        const key = `friendBattle${i}` as keyof Omit<UiRects, 'versusLobbyRows' | 'tokenShopItem'>;
        if (pointInRect(x, y, this.uiRects[key] as Rect | undefined)) {
          this.actions.push("FRIEND_BATTLE");
          this._pendingFriendBattleIndex = i;
          return;
        }
      }
      for (let i = 0; i < 3; i++) {
        const acceptKey = `friendAccept${i}` as keyof Omit<UiRects, 'versusLobbyRows' | 'tokenShopItem'>;
        if (pointInRect(x, y, this.uiRects[acceptKey] as Rect | undefined)) {
          this.actions.push("FRIEND_ACCEPT");
          this._pendingFriendAcceptIndex = i;
          return;
        }
        const declineKey = `friendDecline${i}` as keyof Omit<UiRects, 'versusLobbyRows' | 'tokenShopItem'>;
        if (pointInRect(x, y, this.uiRects[declineKey] as Rect | undefined)) {
          this.actions.push("FRIEND_DECLINE");
          this._pendingFriendDeclineIndex = i;
          return;
        }
      }
      for (let i = 0; i < 5; i++) {
        const key = `friendRemove${i}` as keyof Omit<UiRects, 'versusLobbyRows' | 'tokenShopItem'>;
        if (pointInRect(x, y, this.uiRects[key] as Rect | undefined)) {
          this.actions.push("FRIEND_REMOVE");
          this._pendingFriendRemoveIndex = i;
          return;
        }
      }
      if (pointInRect(x, y, this.uiRects.friendsPagePrev)) {
        this.actions.push("FRIENDS_PAGE_PREV");
        return;
      }
      if (pointInRect(x, y, this.uiRects.friendsPageNext)) {
        this.actions.push("FRIENDS_PAGE_NEXT");
        activeUiHitSlopPx = 0;
        return;
      }
      if (pointInRect(x, y, this.uiRects.keyTrigger)) {
        activeUiHitSlopPx = 0;
        return;
      }

      // Track swipe start for carousel-style pages
      this._uiSwipeStartX = x;

      // Only accept one active pointer for board drags (multi-touch guard)
      activeUiHitSlopPx = 0;
      if (this.activePointerId != null) return;
      this.activePointerId = event.pointerId;
      this.trySetPointerCapture(event.pointerId);
      this.pointerStartCell = this.canvasToBoardCell(x, y);
      } finally {
        activeUiHitSlopPx = 0;
      }
    };
    this.canvas.addEventListener("pointerdown", onPointerDown);
    this.cleanupCallbacks.push(() => this.canvas.removeEventListener("pointerdown", onPointerDown));

    const onPointerUp = (event: PointerEvent): void => {
      this._lastPressedRect = null;
      // â”€â”€ Radial menu release â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
      if (this._radialHoldState != null && event.pointerId === this._radialHoldState.pointerId) {
        const { x, y } = this.toCanvasPoint(event);
        const elapsed = performance.now() - this._radialHoldState.startTimeMs;
        if (elapsed >= RADIAL_HOLD_THRESHOLD_MS || this._radialHoldState.holdReached) {
          const hubRect = this._radialHoldState.kind === "powerup"
            ? this.uiRects.characterHub
            : this.uiRects.opponentHub;
          const hubX = hubRect ? hubRect.x + hubRect.w / 2 : this._radialHoldState.startX;
          const hubY = hubRect ? hubRect.y + hubRect.h / 2 : this._radialHoldState.startY;
          this._pendingRadialRelease = {
            kind: this._radialHoldState.kind,
            pointerX: x,
            pointerY: y,
            hubX,
            hubY
          };
        }
        this.tryReleasePointerCapture(event.pointerId);
        this._radialHoldState = null;
        return;
      }
      if (this.blocked) {
        this.resetPointerTracking();
        return;
      }
      if (this.volumeDragPointerId != null && event.pointerId === this.volumeDragPointerId) {
        const { x } = this.toCanvasPoint(event);
        if (this.volumeDragTarget === "sfx") {
          this.captureSfxVolumeAtX(x);
        } else {
          this.captureMusicVolumeAtX(x);
        }
        this.volumeDragPointerId = null;
        this.volumeDragTarget = null;
        this.tryReleasePointerCapture(event.pointerId);
        return;
      }
      if (this.leaderboardScrollPointerId != null && event.pointerId === this.leaderboardScrollPointerId) {
        const { y } = this.toCanvasPoint(event);
        if (this.leaderboardScrollLastY != null && y !== this.leaderboardScrollLastY) {
          this.leaderboardScrollDeltaY += y - this.leaderboardScrollLastY;
        }
        this.leaderboardScrollPointerId = null;
        this.leaderboardScrollLastY = null;
        this.leaderboardScrollLastInteractionMs = performance.now();
        this.tryReleasePointerCapture(event.pointerId);
        return;
      }
      if (this.activePointerId != null && event.pointerId !== this.activePointerId) return;
      const { x, y } = this.toCanvasPoint(event);
      this.pointerCanvasPos = null;
      this.activePointerId = null;
      this.tryReleasePointerCapture(event.pointerId);

      // UI swipe detection for carousel pages
      if (this._uiSwipeStartX != null) {
        const dx = x - this._uiSwipeStartX;
        this._uiSwipeStartX = null;
        const swipeThreshold = 30;
        if (Math.abs(dx) > swipeThreshold) {
          if (dx < 0 && this.uiRects.soloCarouselNext != null) {
            this.actions.push("SOLO_CAROUSEL_NEXT");
            return;
          }
          if (dx > 0 && this.uiRects.soloCarouselPrev != null) {
            this.actions.push("SOLO_CAROUSEL_PREV");
            return;
          }
          // Shop skin carousel swipe
          if (dx < 0 && this.uiRects.shopNext != null) {
            this.actions.push("SHOP_NEXT");
            return;
          }
          if (dx > 0 && this.uiRects.shopPrev != null) {
            this.actions.push("SHOP_PREV");
            return;
          }
        }
      }

      const start = this.pointerStartCell;
      const startCanvasPos = this.pointerStartCanvasPos;
      const lockedDirection = this.pointerLockedSwipeDirection;
      const end = this.canvasToBoardCell(x, y);
      this.pointerStartCell = null;
      this.pointerStartCanvasPos = null;
      this.pointerLockedSwipeDirection = null;
      if (start == null) {
        return;
      }

      if (lockedDirection != null) {
        const board = this.getBoardBounds();
        const lockedTarget = swipeIntentDirectionToTarget(start, lockedDirection, board.gridSize);
        if (lockedTarget != null && areAdjacent(start, lockedTarget)) {
          const move = { from: start, to: lockedTarget };
          this.swaps.push(move);
          this.immediateGestureCallbacks.onSwapQueued?.(move);
          return;
        }
      }

      if (end != null && start.row === end.row && start.col === end.col) {
        // Fall through to full swipe-intent inference before treating as a click.
      } else if (end != null && areAdjacent(start, end)) {
        const move = { from: start, to: end };
        this.swaps.push(move);
        this.immediateGestureCallbacks.onSwapQueued?.(move);
        return;
      }

      const board = this.getBoardBounds();
      const intentTarget = inferSwipeIntentTarget(
        start,
        startCanvasPos == null ? 0 : x - startCanvasPos.x,
        startCanvasPos == null ? 0 : y - startCanvasPos.y,
        board.gridSize,
        board.tile * SWIPE_INTENT_THRESHOLD_TILE_FRACTION
      );
      if (intentTarget != null && areAdjacent(start, intentTarget)) {
        const move = { from: start, to: intentTarget };
        this.swaps.push(move);
        this.immediateGestureCallbacks.onSwapQueued?.(move);
        return;
      }

      if (end != null) {
        const click = { row: end.row, col: end.col };
        this.boardClicks.push(click);
        this.immediateGestureCallbacks.onBoardClickQueued?.(click);
      }
    };
    this.canvas.addEventListener("pointerup", onPointerUp);
    this.cleanupCallbacks.push(() => this.canvas.removeEventListener("pointerup", onPointerUp));

    const onPointerCancel = (event: PointerEvent): void => {
      this._lastPressedRect = null;
      if (this.blocked) {
        this.resetPointerTracking();
        return;
      }
      if (this.volumeDragPointerId != null && event.pointerId === this.volumeDragPointerId) {
        this.volumeDragPointerId = null;
        this.volumeDragTarget = null;
        this.tryReleasePointerCapture(event.pointerId);
        return;
      }
      if (this.leaderboardScrollPointerId != null && event.pointerId === this.leaderboardScrollPointerId) {
        this.leaderboardScrollPointerId = null;
        this.leaderboardScrollLastY = null;
        this.leaderboardScrollLastInteractionMs = performance.now();
        this.tryReleasePointerCapture(event.pointerId);
        return;
      }
      if (this.activePointerId != null && event.pointerId !== this.activePointerId) return;
      this.pointerStartCell = null;
      this.pointerStartCanvasPos = null;
      this.pointerLockedSwipeDirection = null;
      this.pointerCanvasPos = null;
      this.activePointerId = null;
      this.tryReleasePointerCapture(event.pointerId);
    };
    this.canvas.addEventListener("pointercancel", onPointerCancel);
    this.cleanupCallbacks.push(() => this.canvas.removeEventListener("pointercancel", onPointerCancel));

    const onPointerLeave = (event: PointerEvent): void => {
      if (this._hoveredRect != null) {
        this._hoveredRect = null;
        this.canvas.style.cursor = "";
      }
      if (this.blocked) {
        this.resetPointerTracking();
        return;
      }
      if (this.volumeDragPointerId != null && event.pointerId === this.volumeDragPointerId) {
        this.volumeDragPointerId = null;
        this.volumeDragTarget = null;
        this.tryReleasePointerCapture(event.pointerId);
      }
      if (this.leaderboardScrollPointerId != null && event.pointerId === this.leaderboardScrollPointerId) {
        this.leaderboardScrollPointerId = null;
        this.leaderboardScrollLastY = null;
        this.leaderboardScrollLastInteractionMs = performance.now();
        this.tryReleasePointerCapture(event.pointerId);
      }
      if (this.activePointerId != null && event.pointerId === this.activePointerId) {
        this.pointerStartCell = null;
        this.pointerStartCanvasPos = null;
        this.pointerLockedSwipeDirection = null;
        this.pointerCanvasPos = null;
        this.activePointerId = null;
        this.tryReleasePointerCapture(event.pointerId);
      }
    };
    this.canvas.addEventListener("pointerleave", onPointerLeave);
    this.cleanupCallbacks.push(() => this.canvas.removeEventListener("pointerleave", onPointerLeave));
  }

  private trySetPointerCapture(pointerId: number): void {
    if (typeof this.canvas.setPointerCapture !== "function") {
      return;
    }
    try {
      this.canvas.setPointerCapture(pointerId);
    } catch {
      // no-op
    }
  }

  private tryReleasePointerCapture(pointerId: number): void {
    if (typeof this.canvas.releasePointerCapture !== "function") {
      return;
    }
    try {
      this.canvas.releasePointerCapture(pointerId);
    } catch {
      // no-op
    }
  }

  private clearBufferedInput(): void {
    this.swaps.length = 0;
    this.boardClicks.length = 0;
    this.actions.length = 0;
    this.musicVolumeChanges.length = 0;
    this.sfxVolumeChanges.length = 0;
    this.leaderboardScrollDeltaY = 0;
    this.leaderboardScrollLastInteractionMs = 0;
    this._pendingFriendBattleIndex = null;
    this._pendingFriendAcceptIndex = null;
    this._pendingFriendDeclineIndex = null;
    this._pendingFriendRemoveIndex = null;
    this._pendingShopBuyIndex = null;
    this._pendingShopEquipIndex = null;
    this._pendingShopBuyGemsIndex = null;
    this._pendingProfileEquipIndex = null;
    this._pendingAvatarSelectNftIndex = null;
    this._tappedLobbyIndex = null;
  }

  private resetPointerTracking(): void {
    if (this.activePointerId != null) {
      this.tryReleasePointerCapture(this.activePointerId);
    }
    if (this.volumeDragPointerId != null) {
      this.tryReleasePointerCapture(this.volumeDragPointerId);
    }
    if (this.leaderboardScrollPointerId != null) {
      this.tryReleasePointerCapture(this.leaderboardScrollPointerId);
    }
    this.pointerStartCell = null;
    this.pointerStartCanvasPos = null;
    this.pointerLockedSwipeDirection = null;
    this.pointerCanvasPos = null;
    this.activePointerId = null;
    this.volumeDragPointerId = null;
    this.leaderboardScrollPointerId = null;
    this.leaderboardScrollLastY = null;
  }

  destroy(): void {
    for (const cleanup of this.cleanupCallbacks) {
      cleanup();
    }
    this.cleanupCallbacks.length = 0;
    this.clearBufferedInput();
    this.resetPointerTracking();
  }

  private captureMusicVolumeAtX(x: number): void {
    const rect = this.uiRects.musicVolume;
    if (rect == null || rect.w <= 0) {
      return;
    }
    const normalized = clamp((x - rect.x) / rect.w, 0, 1);
    this.musicVolumeChanges.push(normalized);
  }

  private captureSfxVolumeAtX(x: number): void {
    const rect = this.uiRects.sfxVolume;
    if (rect == null || rect.w <= 0) {
      return;
    }
    const normalized = clamp((x - rect.x) / rect.w, 0, 1);
    this.sfxVolumeChanges.push(normalized);
  }

  private toCanvasPoint(event: PointerEvent): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    const clientWidth = this.canvas.clientWidth > 0 ? this.canvas.clientWidth : rect.width;
    const clientHeight = this.canvas.clientHeight > 0 ? this.canvas.clientHeight : rect.height;
    const scaleX = this.logicalWidth / Math.max(1, clientWidth);
    const scaleY = this.logicalHeight / Math.max(1, clientHeight);
    const clientLeft = this.canvas.clientLeft ?? 0;
    const clientTop = this.canvas.clientTop ?? 0;
    return {
      x: (event.clientX - rect.left - clientLeft) * scaleX,
      y: (event.clientY - rect.top - clientTop) * scaleY - this._nativeSafeAreaTop
    };
  }

  private resolvePointerHitSlop(pointerType: string): number {
    return pointerType === "touch" || pointerType === "pen"
      ? TOUCH_HIT_SLOP_PX
      : DESKTOP_HIT_SLOP_PX;
  }

  private isGameplayBoardInteractionActive(): boolean {
    return this.uiRects.rotateLeft != null
      && this.uiRects.rotateRight != null
      && this.uiRects.grooveMeter != null;
  }

  private updateBoardSwipeIntentLock(x: number, y: number): void {
    if (this.pointerStartCanvasPos == null || this.pointerStartCell == null) {
      this.pointerLockedSwipeDirection = null;
      return;
    }

    const board = this.getBoardBounds();
    const dragDxPx = x - this.pointerStartCanvasPos.x;
    const dragDyPx = y - this.pointerStartCanvasPos.y;
    const neutralRadiusPx = board.tile * SWIPE_INTENT_RELOCK_NEUTRAL_FRACTION;
    if (Math.hypot(dragDxPx, dragDyPx) <= neutralRadiusPx) {
      this.pointerLockedSwipeDirection = null;
      return;
    }

    if (this.pointerLockedSwipeDirection != null) {
      return;
    }

    this.pointerLockedSwipeDirection = resolveSwipeIntentDirection(
      dragDxPx,
      dragDyPx,
      board.tile * SWIPE_INTENT_THRESHOLD_TILE_FRACTION,
      SWIPE_INTENT_AXIS_DOMINANCE_RATIO
    );
  }

  private getBoardBounds(): BoardBounds {
    const boardRect = this.uiRects.board;
    const gridSize = Math.max(1, Math.floor(this.uiRects.boardGridSize ?? BOARD_SIZE));
    if (boardRect != null && boardRect.w > 0 && boardRect.h > 0) {
      const size = Math.min(boardRect.w, boardRect.h);
      return {
        x: boardRect.x,
        y: boardRect.y,
        size,
        tile: size / gridSize,
        gridSize
      };
    }

    return {
      x: activeBoardOriginX(),
      y: getLayout(this.effectiveLayoutHeight).boardOriginY - this._boardYOffset,
      size: activeBoardPixelSize(),
      tile: activeTilePixelSize(),
      gridSize: BOARD_SIZE
    };
  }

  private canvasToBoardCell(x: number, y: number): Position | null {
    const board = this.getBoardBounds();
    if (
      x < board.x ||
      y < board.y ||
      x >= board.x + board.size ||
      y >= board.y + board.size
    ) {
      return null;
    }
    const maxIndex = Math.max(0, board.gridSize - 1);
    const col = clamp(Math.floor((x - board.x) / board.tile), 0, maxIndex);
    const row = clamp(Math.floor((y - board.y) / board.tile), 0, maxIndex);
    return { row, col };
  }

  consumeSwap(): MoveAttempt | null {
    if (this.blocked) {
      this.clearBufferedInput();
      return null;
    }
    return this.swaps.shift() ?? null;
  }

  consumeActions(): UiAction[] {
    if (this.blocked) {
      this.clearBufferedInput();
      return [];
    }
    const out = [...this.actions];
    this.actions.length = 0;
    return out;
  }

  consumeBoardClick(): Position | null {
    if (this.blocked) {
      this.clearBufferedInput();
      return null;
    }
    return this.boardClicks.shift() ?? null;
  }

  consumeRotationSelection(): RotationDir | null {
    return null;
  }

  consumeFriendBattleIndex(): number | null {
    if (this.blocked) {
      this.clearBufferedInput();
      return null;
    }
    const idx = this._pendingFriendBattleIndex;
    this._pendingFriendBattleIndex = null;
    return idx;
  }

  consumeFriendAcceptIndex(): number | null {
    if (this.blocked) {
      this.clearBufferedInput();
      return null;
    }
    const idx = this._pendingFriendAcceptIndex;
    this._pendingFriendAcceptIndex = null;
    return idx;
  }

  consumeFriendDeclineIndex(): number | null {
    if (this.blocked) {
      this.clearBufferedInput();
      return null;
    }
    const idx = this._pendingFriendDeclineIndex;
    this._pendingFriendDeclineIndex = null;
    return idx;
  }

  consumeFriendRemoveIndex(): number | null {
    if (this.blocked) {
      this.clearBufferedInput();
      return null;
    }
    const idx = this._pendingFriendRemoveIndex;
    this._pendingFriendRemoveIndex = null;
    return idx;
  }

  hasAction(action: UiAction): boolean {
    const idx = this.actions.indexOf(action);
    if (idx === -1) return false;
    this.actions.splice(idx, 1);
    return true;
  }

  consumeShopBuyIndex(): number | null {
    if (this.blocked) {
      this.clearBufferedInput();
      return null;
    }
    const idx = this._pendingShopBuyIndex;
    this._pendingShopBuyIndex = null;
    return idx;
  }

  consumeShopEquipIndex(): number | null {
    if (this.blocked) {
      this.clearBufferedInput();
      return null;
    }
    const idx = this._pendingShopEquipIndex;
    this._pendingShopEquipIndex = null;
    return idx;
  }

  consumeShopBuyGemsIndex(): number | null {
    if (this.blocked) {
      this.clearBufferedInput();
      return null;
    }
    const idx = this._pendingShopBuyGemsIndex;
    this._pendingShopBuyGemsIndex = null;
    return idx;
  }

  consumeProfileEquipIndex(): number | null {
    if (this.blocked) {
      this.clearBufferedInput();
      return null;
    }
    const idx = this._pendingProfileEquipIndex;
    this._pendingProfileEquipIndex = null;
    return idx;
  }

  consumeAvatarSelectNftIndex(): number | null {
    if (this.blocked) {
      this.clearBufferedInput();
      return null;
    }
    const idx = this._pendingAvatarSelectNftIndex;
    this._pendingAvatarSelectNftIndex = null;
    return idx;
  }

  consumeTokenShopItemIndex(): number | null {
    if (this.blocked) {
      this.clearBufferedInput();
      return null;
    }
    const idx = this._pendingTokenShopItemIndex;
    this._pendingTokenShopItemIndex = null;
    return idx;
  }

  consumeTappedLobbyIndex(): number | null {
    if (this.blocked) {
      this.clearBufferedInput();
      return null;
    }
    const idx = this._tappedLobbyIndex;
    this._tappedLobbyIndex = null;
    return idx;
  }

  consumeTappedInboxIndex(): number {
    const i = this._tappedInboxIndex;
    this._tappedInboxIndex = -1;
    return i;
  }

  consumeTappedFriendIndex(): number {
    const i = this._tappedFriendIndex;
    this._tappedFriendIndex = -1;
    return i;
  }

  consumeMusicVolumeChange(): number | null {
    if (this.blocked) {
      this.clearBufferedInput();
      return null;
    }
    return this.musicVolumeChanges.shift() ?? null;
  }

  consumeSfxVolumeChange(): number | null {
    if (this.blocked) {
      this.clearBufferedInput();
      return null;
    }
    return this.sfxVolumeChanges.shift() ?? null;
  }

  consumeLeaderboardScrollGesture(): LeaderboardScrollGesture {
    const gesture: LeaderboardScrollGesture = {
      held: this.leaderboardScrollPointerId != null,
      deltaY: this.leaderboardScrollDeltaY,
      lastInteractionMs: this.leaderboardScrollLastInteractionMs
    };
    this.leaderboardScrollDeltaY = 0;
    return gesture;
  }

  getDragState(): DragState | null {
    if (this.blocked) {
      return null;
    }
    if (this.pointerStartCell == null || this.pointerCanvasPos == null) {
      return null;
    }
    return {
      startCell: this.pointerStartCell,
      canvasX: this.pointerCanvasPos.x,
      canvasY: this.pointerCanvasPos.y
    };
  }

  getHoverCell(): Position | null {
    if (this.blocked) {
      return null;
    }
    if (this.pointerStartCell != null || this.pointerCanvasPos == null) {
      return null;
    }
    return this.canvasToBoardCell(this.pointerCanvasPos.x, this.pointerCanvasPos.y);
  }

  getLastPressedRect(): Rect | null {
    return this._lastPressedRect;
  }

  getHoveredRect(): Rect | null {
    return this._hoveredRect;
  }

  getHoverPosition(): { x: number; y: number } | null {
    return this._hoveredRect != null && this.pointerCanvasPos != null
      ? this.pointerCanvasPos
      : null;
  }

  private findHitRect(x: number, y: number): Rect | null {
    const priorityHit = this.resolvePriorityUiHit(x, y);
    if (priorityHit != null) {
      return priorityHit.rect;
    }
    const leaderboardScrollViewport = this.uiRects.leaderboardScrollViewport;
    for (const val of Object.values(this.uiRects)) {
      if (
        val != null &&
        val !== leaderboardScrollViewport &&
        typeof val !== "number" &&
        !Array.isArray(val) &&
        pointInRect(x, y, val as Rect)
      ) {
        return val as Rect;
      }
    }
    return null;
  }

  getHoveredPowerup(): PowerupKind | null {
    // Powerup buttons removed â€” hover detection now handled by radial menu
    return null;
  }

  getRadialMenuState(): { kind: "powerup" | "trap"; hubX: number; hubY: number; pointerX: number; pointerY: number; holdElapsedMs: number; holdReached: boolean } | null {
    if (this._radialHoldState == null) return null;
    const elapsed = performance.now() - this._radialHoldState.startTimeMs;
    const holdReached = elapsed >= RADIAL_HOLD_THRESHOLD_MS || this._radialHoldState.holdReached;
    // Use the hub rect center as hubX/hubY
    const hubRect = this._radialHoldState.kind === "powerup"
      ? this.uiRects.characterHub
      : this.uiRects.opponentHub;
    const hubX = hubRect ? hubRect.x + hubRect.w / 2 : this._radialHoldState.startX;
    const hubY = hubRect ? hubRect.y + hubRect.h / 2 : this._radialHoldState.startY;
    return {
      kind: this._radialHoldState.kind,
      hubX,
      hubY,
      pointerX: this.pointerCanvasPos?.x ?? this._radialHoldState.startX,
      pointerY: this.pointerCanvasPos?.y ?? this._radialHoldState.startY,
      holdElapsedMs: elapsed,
      holdReached
    };
  }

  consumeRadialRelease(): { kind: "powerup" | "trap"; pointerX: number; pointerY: number; hubX: number; hubY: number } | null {
    const pending = this._pendingRadialRelease;
    this._pendingRadialRelease = null;
    return pending;
  }

  hasPendingRadialRelease(): boolean {
    return this._pendingRadialRelease != null;
  }
}
